import { types as sdkTypes } from './sdkLoader';
import { formatMoney } from './currency';
import { PRICE_INPUT_PUBLIC_DATA_KEYS } from './shipping';

const { Money } = sdkTypes;

export const DISPLAY_CURRENCY_EUR = 'EUR';
export const DISPLAY_CURRENCY_USD = 'USD';
export const DISPLAY_CURRENCIES = [DISPLAY_CURRENCY_EUR, DISPLAY_CURRENCY_USD];

export const DISPLAY_CURRENCY_STORAGE_KEY = 'wearby.displayCurrency';

/**
 * Default display currency for new visitors (no stored preference).
 * Always EUR — buyers can switch to USD via the currency switcher.
 * @returns {'EUR'}
 */
export const getDefaultDisplayCurrency = () => DISPLAY_CURRENCY_EUR;

/**
 * @param {string} value
 * @returns {boolean}
 */
export const isValidDisplayCurrency = value => DISPLAY_CURRENCIES.includes(value);

/**
 * Convert money subunits with an FX rate (rounded).
 * @param {number} amountSubunits
 * @param {number} rate
 * @returns {number|null}
 */
export const convertSubunitsWithRate = (amountSubunits, rate) => {
  if (!Number.isFinite(amountSubunits) || !Number.isFinite(rate) || rate <= 0) {
    return null;
  }
  return Math.round(amountSubunits * rate);
};

/**
 * Read stored seller USD input from listing publicData, if present.
 * @param {Object} [publicData]
 * @returns {{ amount: number, currency: 'USD' }|null}
 */
export const getStoredUsdPriceInput = publicData => {
  const currency = publicData?.[PRICE_INPUT_PUBLIC_DATA_KEYS.currency];
  const amount = publicData?.[PRICE_INPUT_PUBLIC_DATA_KEYS.amount];
  if (
    currency === DISPLAY_CURRENCY_USD &&
    typeof amount === 'number' &&
    Number.isFinite(amount) &&
    amount >= 0
  ) {
    return { amount, currency: DISPLAY_CURRENCY_USD };
  }
  return null;
};

const asEstimateLabel = formatted => (formatted ? `≈ ${formatted}` : null);

/**
 * Format a listing settlement price for the buyer's display currency preference.
 * USD amounts (stored seller input or live FX) are always estimates — charge is EUR.
 *
 * @param {Object} params
 * @param {Object} params.intl - react-intl
 * @param {Money} params.price - settlement Money (marketplace currency, typically EUR)
 * @param {Object} [params.publicData] - listing publicData
 * @param {'EUR'|'USD'} params.displayCurrency
 * @param {string} params.marketplaceCurrency - e.g. 'EUR'
 * @param {number|null|undefined} params.eurToUsdRate - live EUR→USD rate
 * @returns {{
 *   label: string|null,
 *   title: string|null,
 *   isEstimate: boolean,
 *   displayCurrency: string,
 *   unsupportedCurrency?: string
 * }}
 */
export const formatListingPriceForDisplay = ({
  intl,
  price,
  publicData,
  displayCurrency,
  marketplaceCurrency,
  eurToUsdRate,
}) => {
  if (!price) {
    return {
      label: null,
      title: null,
      isEstimate: false,
      displayCurrency: displayCurrency || marketplaceCurrency,
    };
  }

  // Settlement currency mismatch with marketplace
  if (marketplaceCurrency && price.currency !== marketplaceCurrency) {
    const code = price.currency;
    return {
      label: intl.formatMessage({ id: 'ListingCard.unsupportedPrice' }, { currency: code }),
      title: intl.formatMessage({ id: 'ListingCard.unsupportedPriceTitle' }, { currency: code }),
      isEstimate: false,
      displayCurrency: code,
      unsupportedCurrency: code,
    };
  }

  if (displayCurrency !== DISPLAY_CURRENCY_USD) {
    const formatted = formatMoney(intl, price);
    return {
      label: formatted,
      title: formatted,
      isEstimate: false,
      displayCurrency: marketplaceCurrency || price.currency,
    };
  }

  // Prefer stored seller USD input, still as estimate (may drift from live EUR→USD).
  const storedUsd = getStoredUsdPriceInput(publicData);
  if (storedUsd) {
    const usdMoney = new Money(storedUsd.amount, DISPLAY_CURRENCY_USD);
    const formatted = formatMoney(intl, usdMoney);
    const label = asEstimateLabel(formatted);
    return {
      label,
      title: intl.formatMessage({ id: 'ListingCard.priceEstimateTooltip' }, { price: label }),
      isEstimate: true,
      displayCurrency: DISPLAY_CURRENCY_USD,
    };
  }

  // Live FX estimate from settlement EUR → USD
  if (price.currency === DISPLAY_CURRENCY_EUR && Number.isFinite(eurToUsdRate) && eurToUsdRate > 0) {
    const convertedAmount = convertSubunitsWithRate(price.amount, eurToUsdRate);
    if (convertedAmount != null) {
      const usdMoney = new Money(convertedAmount, DISPLAY_CURRENCY_USD);
      const formatted = formatMoney(intl, usdMoney);
      const label = asEstimateLabel(formatted);
      return {
        label,
        title: intl.formatMessage({ id: 'ListingCard.priceEstimateTooltip' }, { price: label }),
        isEstimate: true,
        displayCurrency: DISPLAY_CURRENCY_USD,
      };
    }
  }

  // Rate missing: fall back to settlement rather than a wrong USD number
  const formatted = formatMoney(intl, price);
  return {
    label: formatted,
    title: formatted,
    isEstimate: false,
    displayCurrency: price.currency,
  };
};

/**
 * Format an arbitrary settlement Money (e.g. payinTotal) as a USD amount string (no ≈ prefix).
 * Callers that want estimate wording should wrap via intl ("Approx. {amount}").
 *
 * @param {Object} params
 * @param {Object} params.intl
 * @param {Money} params.money
 * @param {number|null|undefined} params.eurToUsdRate
 * @returns {string|null}
 */
export const formatMoneyAsUsdEstimate = ({ intl, money, eurToUsdRate }) => {
  if (!money || money.currency !== DISPLAY_CURRENCY_EUR) {
    return null;
  }
  if (!Number.isFinite(eurToUsdRate) || eurToUsdRate <= 0) {
    return null;
  }
  const convertedAmount = convertSubunitsWithRate(money.amount, eurToUsdRate);
  if (convertedAmount == null) {
    return null;
  }
  const usdMoney = new Money(convertedAmount, DISPLAY_CURRENCY_USD);
  return formatMoney(intl, usdMoney);
};
